/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import classNames from 'classnames';
import { KeyboardEvent, useEffect, useRef, useState } from 'react';
import { toast as libToast } from 'react-hot-toast';
import Button from '../Button/Button';
import { ButtonVariants } from '../Button/Button.types';
import Icon from '../Icons/Icon';
import styles from './Toast.module.css';
import { ToastElementProps, ToastTypes } from './Toast.types';

/**
 * This component is rendered using the `toast` function exported from 'components/Toast/Toast' - it should not be used individually (there are many required props created by react-hot-toast)
 */
const ToastElement: React.FC<ToastElementProps> = ({
  label,
  buttonLabel,
  onButtonClick,
  toastType,
  id,
  startPause,
  endPause,
  visible,
  subtext,
}) => {
  const shouldShowButton = onButtonClick !== undefined && buttonLabel !== undefined;
  const buttonRef = useRef<HTMLButtonElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [previousFocus] = useState(document.activeElement);

  const dismissThisToast = () => {
    libToast.dismiss(id);
  };

  const unpause = () => {
    // does not stop pause if there is a cta or if elements within the toast have focus
    if (shouldShowButton || document.activeElement === buttonRef.current) {
      return;
    }
    if (endPause) {
      endPause();
    }
  };

  const pause = () => {
    if (startPause) {
      startPause();
    }
  };

  // locks the focus within the dialog
  const toggleFocus = (e: KeyboardEvent) => {
    e.preventDefault();
    if (e.target === closeButtonRef.current) {
      buttonRef.current?.focus();
    } else {
      closeButtonRef.current?.focus();
    }
  };

  const handleFocus = () => {
    pause();
  };

  const handleBlur = () => {
    unpause();
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    switch (e.key) {
      case 'Escape':
        dismissThisToast();
        break;
      case 'Tab':
        toggleFocus(e);
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (visible) {
      // if there is a CTA, focus CTA
      if (buttonRef.current !== null) buttonRef.current.focus();
      // if there is no CTA + triggerFocus prop is true, focus close button
      if (closeButtonRef.current && buttonRef.current === null) closeButtonRef.current.focus();
      // when the ToastElement is no longer visible, refocus the element that was focused when it was opened
    } else {
      (previousFocus as HTMLElement)?.focus();
    }
  }, [visible, previousFocus]);

  const renderIcon = () => {
    switch (toastType) {
      case ToastTypes.Info:
        return <Icon iconName="Info" className={classNames(styles.icon, styles.info)} />;
      case ToastTypes.Error:
        return <Icon iconName="AlertCircle" className={classNames(styles.icon, styles.error)} />;
      case ToastTypes.Success:
      default:
        return <Icon iconName="CheckCircle" className={classNames(styles.icon, styles.success)} />;
    }
  };

  return (
    <div
      className={classNames(styles.toastElementContainer, {
        [styles.error]: toastType === ToastTypes.Error,
        [styles.info]: toastType === ToastTypes.Info,
        [styles.success]: toastType === ToastTypes.Success,
        [styles.visible]: visible,
      })}
      role="alertdialog"
      aria-describedby={`${id}-message`}
      aria-modal={visible}
      ref={containerRef}
      onKeyDown={handleKeyDown}
      onMouseEnter={pause}
      onMouseLeave={unpause}
      data-testid="ToastElement"
    >
      <div className={styles.iconAndMessage}>
        {renderIcon()}
        <div className={styles.messageContainer}>
          <div className={styles.message} id={`${id}-message`}>
            {label}
          </div>
          {subtext && <div className={styles.subtext}>{subtext}</div>}
          {shouldShowButton && (
            <div className={styles.buttonWrapper}>
              <Button
                variant={ButtonVariants.Text}
                label={buttonLabel}
                ref={buttonRef}
                onFocus={handleFocus}
                onBlur={handleBlur}
                onClick={onButtonClick}
              />
            </div>
          )}
        </div>
      </div>
      <button
        className={styles.closeButton}
        ref={closeButtonRef}
        onClick={dismissThisToast}
        onFocus={handleFocus}
        onBlur={handleBlur}
        aria-label="close"
        type="button"
      >
        <Icon iconName="X" />
      </button>
    </div>
  );
};

export default ToastElement;
